<template>
  <b-container class="chart-tab" fluid>
    <div class="chart-tab__filters">
      <label for="view-select">
        {{ $t('engineering.isolation-aging.select-view') }}
        <info-badge v-show="isHistogram" :string-key="titleTooltip" />
      </label>
      <s-enum-select
        id="view-select"
        v-model="selectedVision"
        no-select-option
        no-sort
        translation-path="enums.engineering.isolation-aging.visions"
        :disabled="loadingChart"
        :enum-definition="visions"
        @input="onUpdateFilter"
      />
      <label for="criteria" class="mt-3">
        {{ $t('engineering.isolation-aging.end-life-criteria') }}
      </label>
      <s-select
        id="criteria"
        class="select"
        v-model="selectedCriteria"
        value-field="id"
        text-field="name"
        :disabled="loadingCriteria"
        :options="criteria"
        @input="onUpdateFilter"
      >
        <div slot="item" slot-scope="data">
          <i v-show="data.alarmed" class="fas fa-exclamation-triangle" aria-hidden="true" />
          <span>{{ data.name }}</span>
        </div>
      </s-select>
    </div>
    <div class="chart-tab__chart-wrapper">
      <default-chart
        chart-id="isolation-aging"
        :x-data-manager="xDataManager"
        :chart-type="chartType"
        :key="selectedVision"
        :chartData="chartData"
        :loading="loadingChart"
        :remove-outlier.sync="removeChartOutlier"
        :enable-multi-axis.sync="enableChartMultiAxis"
        @toggle-outlier="getChartData(true)"
        @toggle-multi-axis="getChartData(true)"
      />
    </div>
  </b-container>
</template>

<script>
import chartVisions from '@/domain/enums/installation/engineering/isolationAging/chartVisions';
import DefaultChart from '@/components/charts/DefaultChart';
import InfoBadge from '@/components/icon/InfoBadge';
import defaultChartMixin from '@/domain/mixins/defaultChartMixin';
import eventBus from '@/services/globalEventBus';
import Service
  from '@/domain/services/installation/engineering/isolationAging/isolationAgingService';
import chartTypes from '@/domain/enums/chartTypes';
import events from '@/domain/events/engineering';

const HISTOGRAM_MESSAGE_ID = 'histogram-message';

export default {
  name: 'IsolationAgingChartTab',
  props: {
    tabInfo: {
      type: Object,
      default: () => ({}),
    },
  },
  components: {
    DefaultChart,
    InfoBadge,
  },
  mixins: [defaultChartMixin],
  data: vm => ({
    visions: chartVisions,
    selectedVision: chartVisions.HISTOGRAM,
    loadingCriteria: false,
    criteria: [],
    selectedCriteria: vm.tabInfo.selectedCriteria,
    chartData: [],
    footerMessage: vm.createFooterMessage(),
    chartTypes,
  }),
  computed: {
    titleTooltip() {
      return this.$t('messages.MI039.message', { timezone: this.tabInfo.installationTimeZone });
    },
    isHistogram() {
      return this.selectedVision === chartVisions.HISTOGRAM;
    },
    chartType() {
      return this.isHistogram ? chartTypes.BAR : chartTypes.LINE;
    },
    xDataManager() {
      return this.isHistogram ? undefined : 'dateTime';
    },
  },
  created() {
    this.initListener();
    this.getChartData(true);
  },
  mounted() {
    this.appendFooterMessage();
  },
  beforeDestroy() {
    this.removeListener();
  },
  methods: {
    initListener() {
      eventBus.on(events.refreshDataTab, this.getChartData, this);
    },
    removeListener() {
      eventBus.off(events.refreshDataTab, this.getChartData, this);
    },
    appendFooterMessage() {
      const element = document.getElementsByClassName('e-chart')[0];
      if (!element) return;
      element.firstElementChild.appendChild(this.footerMessage);
    },
    createFooterMessage() {
      const message = document.createElement('div');
      message.innerText = this.$t('engineering.isolation-aging.histogram-chart-message');
      message.id = HISTOGRAM_MESSAGE_ID;
      message.className = 'chart-tab__footer-message';
      return message;
    },
    handleMessageVisibility() {
      this.appendFooterMessage();
      const footerMessage = document.getElementById(HISTOGRAM_MESSAGE_ID) || {};
      if (!this.isHistogram || !this.chartData.length) {
        footerMessage.className = 'chart-tab__footer-message invisible';
        return;
      }
      footerMessage.className = 'chart-tab__footer-message visible';
    },
    onUpdateFilter() {
      this.getChartData(true);
    },
    mapHistogramData(data = []) {
      return data.map((item) => {
        const values = (item.values || []).map((value) => {
          const hour = value.x.split(':');
          return {
            x: `${hour[0]}:00 - ${hour[0]}:59`,
            y: value.y,
          };
        });
        values.sort((first, second) => first.x.localeCompare(second.x));
        return {
          ...item,
          values,
        };
      });
    },
    getCriteria(showLoading) {
      this.loadingCriteria = showLoading;
      Service.getCriterion(this.$route.params.assetModuleId)
        .then((response) => {
          this.criteria = response;
        }).finally(() => {
        this.loadingCriteria = false;
      });
    },
    getHistogram(showLoading) {
      this.loadingChart = showLoading;
      return Service.getCriticalHourHistogramChart(
        this.$route.params.assetModuleId,
        {
          ...this.chartFilters,
          criteriaId: this.selectedCriteria,
        },
      ).then((response) => {
        this.chartData = this.mapHistogramData(response);
      }).finally(() => {
        this.handleMessageVisibility();
        this.loadingChart = false;
      });
    },
    getIsolationAgingData(showLoading) {
      this.loadingChart = showLoading;
      return Service.getAgingChart(
        this.$route.params.assetModuleId,
        {
          ...this.chartFilters,
          criteriaId: this.selectedCriteria,
        },
      ).then((response) => {
        this.chartData = response;
      }).finally(() => {
        this.handleMessageVisibility();
        this.loadingChart = false;
      });
    },
    getChartData(showLoading) {
      if (!this.tabInfo.windingAmount) return;

      this.getCriteria(showLoading);

      if (this.isHistogram) {
        this.getHistogram(showLoading);
        return;
      }
      this.getIsolationAgingData(showLoading);
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/mobile-first"

.chart-tab__filters
  display: flex
  flex-direction: column
  margin-bottom: 15px

.chart-tab__footer-message
  position: absolute
  right: 15px
  bottom: 0
  font-size: .8rem

.e-chart
  padding-bottom: 8px

.dropdown-item i
  color: var(--color-danger)

.dropdown-item:hover i
  color: white

.manage-fonts_18px .chart-tab,
.manage-fonts_16px .chart-tab
  +media-max-md
    .chart-tab__footer-message
      bottom: -22px

    .e-chart
      padding-bottom: 25px
</style>
